import PropTypes from 'prop-types';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import {
  ActionIcon,
  Button,
  Flex,
  Grid,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import s3Mutations from '@/api/mutitations/s3';
import { useDisclosure } from '@mantine/hooks';
import prepareTranslate from '@/helpers/dictionary';
import { useNavigate, useParams } from 'react-router-dom';
import { routeWithParams } from '@/helpers/route';
import { useState } from 'react';
import {
  AGENT_STATUS,
  AGENT_TYPE,
  EMPLOYEE_TYPE,
  FAMILY_STATUS,
  GENDER,
} from '@/helpers/enums';
import routes from '@/routes';
import { DatePickerInput } from '@mantine/dates';
import Section from '../Section';
import PageTemplate from '../PageTemplate';
import SectionActions from '../SectionActions';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';
import DetailsModal from '../DetailsModal';

const EditAgentDetails = (props) => {
  const {
    form,
    handleAgentUpdateOrSaveClick = {},
    handleBackClick = {},
    handleDeleteAgent = {},
    loading,
    onError,
    setRefreshKey,
  } = props;

  const isCreateMode = window.location.pathname === routes.agentCreate.path;
  const navigate = useNavigate();

  const t = prepareTranslate();
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure(false);
  const { id } = useParams();

  const { useUpload, useDelete } = s3Mutations;
  const { mutateAsync: uploadMutate } = useUpload();
  const { mutateAsync: deleteMutate } = useDelete();

  const handleSubmit = async (values) => {
    const { errors } = form.validate();
    const relevantTopLevel = ['status', 'agentType', 'employeeType'];
    const hasError = Object.keys(errors).some(
      (errorKey) => errorKey.startsWith('personalDetails.') || relevantTopLevel.includes(errorKey),
    );

    if (hasError) {
      return;
    }

    try {
      filesToDelete.forEach((file) => {
        const { path } = file;
        deleteMutate({ path });
      });

      const {
        policyEndDateFiles, taxDeductionsDateFiles, agentLicenseDateFile,
      } = values.personalDetails;
      const newPolicyEndDateFiles = policyEndDateFiles?.filter((file) => !file.path);
      const newTaxDeductionsDateFiles = taxDeductionsDateFiles?.filter((file) => !file.path);
      const newAgentLicenseExpirationDdatev = agentLicenseDateFile?.filter((file) => !file.path);

      const clonedValues = { ...values };
      const { personalDetails } = clonedValues;

      if (newPolicyEndDateFiles?.length > 0) {
        const formData = new FormData();
        newPolicyEndDateFiles.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          const uploadResult = await uploadMutate(formData);

          const oldFiles = policyEndDateFiles.filter((file) => file.path);
          personalDetails.policyEndDateFiles = [...uploadResult.files, ...oldFiles];
        } catch (error) {
          return;
        }
      }

      if (newTaxDeductionsDateFiles?.length > 0) {
        const formData = new FormData();
        newTaxDeductionsDateFiles.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          const uploadResult = await uploadMutate(formData);
          const oldFiles = taxDeductionsDateFiles.filter((file) => file.path);
          personalDetails.taxDeductionsDateFiles = [...uploadResult.files, ...oldFiles];
        } catch (error) {
          return;
        }
      }

      if (newAgentLicenseExpirationDdatev?.length > 0) {
        const formData = new FormData();
        newAgentLicenseExpirationDdatev.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          const uploadResult = await uploadMutate(formData);
          const oldFiles = agentLicenseDateFile.filter((file) => file.path);
          personalDetails.agentLicenseDateFile = [...uploadResult.files, ...oldFiles];
        } catch (error) {
          return;
        }
      }
      delete personalDetails?.createdAt;
      delete personalDetails?.updatedAt;
      delete personalDetails?.homeFullAddress;
      delete personalDetails?.officeFullAddress;
      delete personalDetails?.fullName;

      const { success } = await handleAgentUpdateOrSaveClick({
        personalDetails,
        status: clonedValues.status,
        agentType: clonedValues.agentType,
        employeeType: clonedValues.employeeType,
      });
      if (!success) {
        onError?.();
        return;
      }
      if (!isCreateMode) {
        navigate(routeWithParams(routes.agentView.path, { id, tab: 'details' }));
        setRefreshKey((prev) => prev + 1);
      }
    } catch (err) {
      onError?.();
    }
  };

  const rightActions = [
    <Button
      key='primary'
      loading={loading}
      w='7rem'
      onClick={() => handleSubmit(form.values, 'personalDetails')}
    >
      {t(isCreateMode ? 'global.save' : 'global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      w='7rem'
      onClick={handleBackClick}
    >
      {t('global.cancel')}
    </Button>,
  ];

  const leftActions = !isCreateMode
    ? [
      <Button
        key="delete"
        variant="outline"
        onClick={deleteOpen}
        leftSection={<IconTrash stroke={2} />}
      >
        {t('pages.agentEdit.delete')}
      </Button>,
    ]
    : [];

  // TO DO REMOVE THAT
  const today = new Date();
  const twentyYearsAgo = new Date(
    today.getFullYear() - 20,
    today.getMonth(),
    today.getDate(),
  );

  const onFileSelected = (files, fieldName) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    const currentFiles = form.values.personalDetails[fieldName] || [];
    const updatedFiles = [...currentFiles, ...formattedFiles];
    form.setFieldValue(`personalDetails.${fieldName}`, updatedFiles);
  };

  const onFileNameChange = (index, newName, fieldName) => {
    const currentFiles = form.values.personalDetails[fieldName] || [];
    const updated = [...currentFiles];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue(`personalDetails.${fieldName}`, updated);
  };

  const onDeleteFile = (index, fieldName) => {
    const currentFiles = form.values.personalDetails[fieldName] || [];
    const updatedFiles = [...currentFiles];
    const [fileToRemove] = updatedFiles.splice(index, 1);
    form.setFieldValue(`personalDetails.${fieldName}`, updatedFiles);
    if (fileToRemove?.path) {
      setFilesToDelete((prev) => [...prev, fileToRemove]);
    }
  };

  const handleDelete = async () => {
    const { sucess } = await handleDeleteAgent();

    if (sucess) {
      deleteClose();
    }
  };

  const deleteActions = [
    <Button key='delete' w={'6rem'} onClick={handleDelete}>
      {t('global.delete')}
    </Button>,
    <Button key='cancel' w={'6rem'} onClick={() => deleteClose()} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  const expiredDate = (date) => {
    if (!date) return 'default';

    const dateValue = new Date(date);
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

    if (dateValue < oneMonthFromNow) {
      return 'error';
    }

    return 'default';
  };

  return (
    <>
      <form>
        <PageTemplate.Panel id='details'>
          <Section title={t('components.editAgentDetails.headers.private')}>
            <Grid gutter='lg'>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.firstname')}
                  required
                  key={form.key('firstName')}
                  {...form.getInputProps('personalDetails.firstName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.lastname')}
                  required
                  key={form.key('lastName')}
                  {...form.getInputProps('personalDetails.lastName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.uid')}
                  required
                  key={form.key('uid')}
                  {...form.getInputProps('personalDetails.uid')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <Select
                  label={t('components.editAgentDetails.gender')}
                  data={GENDER}
                  key={form.key('gender')}
                  required
                  {...form.getInputProps('personalDetails.gender')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  maxDate={twentyYearsAgo}
                  autoComplete='off'
                  label={t('components.editAgentDetails.dob')}
                  required
                  key={form.key('personalDetails.dob')}
                  {...form.getInputProps('personalDetails.dob')}
                  onChange={(e) => {
                    const v = e.target.value
                      .replace(/^(\d\d)(\d)$/g, '$1/$2')
                      .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
                      .replace(/[^\d/]/g, '');
                    form.setFieldValue('personalDetails.dob', v);
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <Select
                  label={t('components.editAgentDetails.status')}
                  data={AGENT_STATUS}
                  required
                  key={form.key('status')}
                  {...form.getInputProps('status')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <Select
                  label={t('components.editAgentDetails.agentType')}
                  data={AGENT_TYPE}
                  required
                  key={form.key('agentType')}
                  {...form.getInputProps('agentType')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <Select
                  label={t('components.editAgentDetails.employeeType')}
                  data={EMPLOYEE_TYPE}
                  required
                  key={form.key('employeeType')}
                  {...form.getInputProps('employeeType')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <Select
                  label={t('components.editAgentDetails.familystatus')}
                  data={FAMILY_STATUS}
                  key={form.key('familyStatus')}
                  {...form.getInputProps('personalDetails.familyStatus')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.spouse')}
                  key={form.key('spouse')}
                  {...form.getInputProps('personalDetails.spouse')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.spousePhone')}
                  key={form.key('spousePhone')}
                  {...form.getInputProps('personalDetails.spousePhone')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.spouseEmail')}
                  key={form.key('spouseEmail')}
                  {...form.getInputProps('personalDetails.spouseEmail')}
                />
              </Grid.Col>
            </Grid>
          </Section>
          <Section title={t('components.editAgentDetails.headers.contact')}>
            <Grid gutter='lg' pl={'10rem'}>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.cellphone')}
                  key={form.key('phone')}
                  {...form.getInputProps('personalDetails.phone')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.extracellphone')}
                  maxLength={10}
                  key={form.key('personalDetails.extraPhone')}
                  {...form.getInputProps('personalDetails.extraPhone')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.homephone')}
                  key={form.key('homePhone')}
                  {...form.getInputProps('personalDetails.homePhone')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.officephone')}
                  key={form.key('officePhone')}
                  {...form.getInputProps('personalDetails.officePhone')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.email')}
                  key={form.key('email')}
                  {...form.getInputProps('personalDetails.email')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.homeaddress')}
                  key={form.key('homeAddress')}
                  {...form.getInputProps('personalDetails.homeAddress')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.officeaddress')}
                  key={form.key('officeAddress')}
                  {...form.getInputProps('personalDetails.officeAddress')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  autoComplete='off'
                  label={t('components.editAgentDetails.website')}
                  key={form.key('website')}
                  {...form.getInputProps('personalDetails.website')}
                />
              </Grid.Col>
            </Grid>
          </Section>

          <Section title={t('components.editAgentDetails.headers.childrens')}>
            <Grid gutter='lg'>
              <Grid.Col span={{ base: 12 }}>
                {form.getValues().personalDetails?.children?.map((_, index) => (
                  <Grid align='center' key={index + 1}>
                    <Grid.Col span={{ base: 12, md: 3 }}>
                      <TextInput
                        autoComplete='off'
                        maxLength={25}
                        required
                        label={t('components.editAgentDetails.fullName')}
                        key={form.key(`personalDetails.children.${index}.childrenName`)}
                        {...form.getInputProps(`personalDetails.children.${index}.childrenName`)}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 3 }}>
                      <TextInput
                        autoComplete='off'
                        maxLength={10}
                        label={t('components.editAgentDetails.dob')}
                        key={form.key(`personalDetails.children.${index}.childrenDob`)}
                        {...form.getInputProps(`personalDetails.children.${index}.childrenDob`)}
                        onChange={(e) => {
                          const v = e.target.value
                            .replace(/^(\d\d)(\d)$/g, '$1/$2')
                            .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
                            .replace(/[^\d/]/g, '');
                          form.setFieldValue(`personalDetails.children.${index}.childrenDob`, v);
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 1 }}>
                      <ActionIcon
                        mt={25}
                        onClick={() => {
                          form.removeListItem('personalDetails.children', index);
                        }}
                        color={'red'}
                        variant={'transparent'}
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Grid.Col>
                  </Grid>
                ))}
                <Button
                  variant='secondary'
                  leftSection={<IconPlus size={16}/>}
                  mt={15}
                  mb={15}
                  onClick={() => {
                    form.insertListItem('personalDetails.children', {
                      childrenName: '',
                      childrenDob: null,
                    });
                  }}
                >
                  <Text size='xs' fw={600}> {t('components.editAgentDetails.addChildren')}</Text>
                </Button>
              </Grid.Col>
            </Grid>
          </Section>

          <Section title={t('components.editAgentDetails.headers.adminDetails')}>
            <Grid gutter='lg'>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <DatePickerInput
                  variant={expiredDate(form.values.personalDetails.policyEndDate)}
                  label={t('components.editAgentDetails.policyend')}
                  key={form.key('personalDetails.policyEndDate')}
                  {...form.getInputProps('personalDetails.policyEndDate')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <DatePickerInput
                  variant={expiredDate(form.values.personalDetails.taxDeductionsDate)}
                  label={t('components.editAgentDetails.tax')}
                  key={form.key('personalDetails.taxDeductionsDate')}
                  {...form.getInputProps('personalDetails.taxDeductionsDate')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <DatePickerInput
                  variant={expiredDate(form.values.personalDetails.agentLicenseDate)}
                  label={t('components.editAgentDetails.license')}
                  key={form.key('personalDetails.agentLicenseDate')}
                  {...form.getInputProps('personalDetails.agentLicenseDate')}
                />
              </Grid.Col>
            </Grid>
            <Grid gutter='lg'>
              <Grid.Col span={{ base: 3, md: 3 }}>
                {form.values.personalDetails?.policyEndDateFiles?.length > 0 && (
                  <EditFiles
                    files={form.values.personalDetails.policyEndDateFiles}
                    onNameChange={(index, newName) => onFileNameChange(index, newName, 'policyEndDateFiles')}
                    onRemove={(index) => onDeleteFile(index, 'policyEndDateFiles')}
                  />
                )}
                <UploadFilesButton
                  onFilesSelected={(files) => onFileSelected(files, 'policyEndDateFiles')}
                  label={t('global.upload')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                {form.values.personalDetails?.taxDeductionsDateFiles?.length > 0 && (
                  <EditFiles
                    files={form.values.personalDetails.taxDeductionsDateFiles}
                    onNameChange={(index, newName) => onFileNameChange(index, newName, 'taxDeductionsDateFiles')}
                    onRemove={(index) => onDeleteFile(index, 'taxDeductionsDateFiles')}
                  />
                )}
                <UploadFilesButton
                  onFilesSelected={(files) => onFileSelected(files, 'taxDeductionsDateFiles')}
                  label={t('global.upload')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                {form.values.personalDetails?.agentLicenseDateFile?.length > 0 && (
                  <EditFiles
                    files={form.values.personalDetails.agentLicenseDateFile}
                    onNameChange={(index, newName) => onFileNameChange(index, newName, 'agentLicenseDateFile')}
                    onRemove={(index) => onDeleteFile(index, 'agentLicenseDateFile')}
                  />
                )}
                <UploadFilesButton
                  onFilesSelected={(files) => onFileSelected(files, 'agentLicenseDateFile')}
                  label={t('global.upload')}
                />
              </Grid.Col>
            </Grid>
          </Section>
          <Flex justify="space-between" mt="sm">
            {rightActions.length > 0 && (
              <SectionActions
                actions={rightActions}
              />
            )}
            {leftActions.length > 0 && (
              <SectionActions
                actions={leftActions}
              />
            )}
          </Flex>
        </PageTemplate.Panel>
      </form>
      <DetailsModal
        opened={deleteOpened}
        close={deleteClose}
        label={t('components.editAgentDetails.verifyDeletion')}
        actions={deleteActions}
      >
        <TextInput
          required
          {...form.getInputProps('deletionReason')}
          label={t('components.editAgentDetails.deletionReason')}
        />
      </DetailsModal>
    </>
  );
};

EditAgentDetails.propTypes = {
  form: PropTypes.object,
  handleAgentUpdateOrSaveClick: PropTypes.func,
  handleBackClick: PropTypes.func,
  handleDeleteAgent: PropTypes.func,
  loading: PropTypes.bool,
  onError: PropTypes.func,
  setRefreshKey: PropTypes.func,
};

export default EditAgentDetails;
